import { Navigate, useLocation } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { isAdminHost } from '../lib/host';
import { lastOrgLanding, readLastOrg } from '../lib/last-org';
import { orgHref } from '../lib/org';
import { useSessionMe } from '../lib/session';

/**
 * Catch-all for bare paths (pre-#460 links, a sign-in's landing, a link the API
 * mints without an org): `/fleet` → `/org/<org>/fleet`. The org, in order:
 *
 *   1. the one the `dz_last_org` cookie names (#466 § 6, `lib/last-org.ts`) —
 *      the last org the person CHOSE — when it is a membership this session can
 *      open; a stale or pinned-away org is ignored;
 *   2. the session's `active_account_id` from `/v1/me` — the account the session
 *      resolves to, NOT a "last worked in" pointer: the URL-scoped app (#460)
 *      never moves it (`POST /v1/me/active-account` has no caller here);
 *   3. the resolved account's login when the credential has no memberships
 *      (PAT / machine credential).
 *
 * Membership login preferred, account id as the URL fallback. Reads the SESSION
 * probe (`useSessionMe`) — no URL org is in play here — and renders nothing
 * until it resolves (it is the key <App/>'s auth gate already holds, so this is
 * instant in practice). An explicit `/org/<login>` URL never reaches this route.
 */
export function LegacyRedirect(props: { token?: string | null }): JSX.Element {
  const me = useSessionMe(props.token !== undefined ? props.token : undefined);
  const loc = useLocation();

  const targetOrg = (): string | null => {
    const m = me.data;
    if (!m) return null;
    const remembered = lastOrgLanding(m.memberships ?? [], readLastOrg());
    if (remembered !== null) return remembered;
    const active = m.memberships?.find((x) => x.account_id === m.active_account_id);
    return active?.github_login ?? m.github_login ?? m.active_account_id ?? null;
  };

  const href = (): string | null => {
    // #754: on admin.developerz.ai a bare/legacy path lands on the radar, not an
    // org home — the admin host carries no org context. No `me` needed.
    if (isAdminHost()) return `/admin${loc.search}`;
    const org = targetOrg();
    if (org === null) return null;
    // A stray `/org` / `/org/` (no login) re-roots at the org home rather than
    // nesting another `/org` segment. `/login` too: signed-out users see the
    // sign-in screen at that URL via the <App/> gate, so a signed-in visit
    // lands in the app instead of an org-scoped NotFound (#475).
    // `/login` AND its #753 leg deep-links (/login/email, /login/sso) re-root:
    // a signed-in visit to any sign-in URL lands in the app, not a NotFound.
    const path =
      loc.pathname === '/' ||
      loc.pathname.startsWith('/org') ||
      loc.pathname === '/login' ||
      loc.pathname.startsWith('/login/')
        ? '/'
        : loc.pathname;
    return `${orgHref(path, org)}${loc.search}`;
  };

  return <Show when={href()}>{(h) => <Navigate href={h()} />}</Show>;
}
